import type { Inventory } from './';

export const inventoryModifiers = (
  Inventory: Pick<Inventory, 'ruleset' | 'getters'>,
) => (state: Inventory.MutableState) => ({
  /**
   * Add points
   */
  addPoints(amount: number) {
    if (amount < 0) throw new Error('Negative addPoints amount');

    state.inventory.points += amount;
  },

  /**
   * Consume points
   */
  consumePoints(amount: number) {
    if (amount < 0) throw new Error('Negative consumePoints amount');

    // Check if we have enough points
    const { inventory } = state;
    const sum = inventory.points;
    if (sum < amount) throw new Error('Not enough points to consume');

    inventory.points -= amount;
  },

  /**
   * Set points
   */
  setPoints(amount: number) {
    if (amount < 0) throw new Error('Negative setPoints amount');

    state.inventory.points = amount;
  },

  /**
   * Add free gems
   */
  addFreeGems(amount: number) {
    if (amount < 0) throw new Error('Negative addFreeGems amount');

    state.inventory.premium.free += amount;
  },

  /**
   * Set free gems
   */
  setFreeGems(amount: number) {
    if (amount < 0) throw new Error('Negative setFreeGems amount');

    state.inventory.premium.free = amount;
  },

  /**
   * Add paid gems
   */
  addPaidGems(amount: number) {
    if (amount < 0) throw new Error('Negative addPaidGems amount');

    state.inventory.premium.paid += amount;
  },

  /**
   * Consume gems, paid gems should be consumed first
   */
  consumeGems(amount: number) {
    if (amount < 0) throw new Error('Negative amount amount');

    // Check if we have enough gems
    const { inventory } = state;
    const sum = inventory.premium.paid + inventory.premium.free;
    if (sum < amount) throw new Error('Not enough gems to consume');

    // Consume paid first
    const paid = Math.min(inventory.premium.paid, amount);
    const free = Math.max(amount - paid, 0);

    inventory.premium.paid -= paid;
    inventory.premium.free -= free;
  },

  /**
   * Add energy
   */
  addEnergy(amount: number, now: number) {
    if (amount === undefined || amount === null)
      throw new Error('addEnergy: Can not be undefined or null');

    if (isNaN(amount)) throw new Error('addEnergy: Can not be NaN');

    // Make sure that amount of add energy more then 0
    if (amount < 0) throw new Error('addEnergy: Amount cannot be less then 0');

    // How many energy was before update
    const previousEnergy = Inventory.getters(state).getEnergy(now);

    // Update energy
    const { inventory } = state;
    inventory.energy = previousEnergy + amount;

    // Update energy regeneration start time so next time only 1 energy is regenerated
    const duration =
      (now - state.inventory.energyRechargeStartTime) %
      Inventory.getters(state).getEnergyRegenerationConfig().time;

    // Update recharge time
    inventory.energyRechargeStartTime = now - duration;
  },

  /**
   * Consume energy
   */
  consumeEnergy(amount: number, now: number) {
    // Make sure amount is positive.
    if (amount < 0)
      throw new Error('consumeEnergy: Amount cannot be less then 0');

    // Energy before update.
    const previousEnergy = Inventory.getters(state).getEnergy(now);

    // Make sure there is enough energy to consume.
    if (amount > previousEnergy)
      throw new Error('consumeEnergy: energy not enough');

    // Decrease energy.
    const { inventory } = state;
    inventory.energy = Math.max(0, previousEnergy - amount);

    // Check if regeneration is starting now.
    const { maxEnergy } = Inventory.ruleset;
    const wasFull = previousEnergy >= maxEnergy;
    const isFull = inventory.energy >= maxEnergy;

    // If regeneration is starting now, set start time accordingly.
    if (wasFull && !isFull) {
      inventory.energyRechargeStartTime = now;
      return;
    }

    // Otherwise should update start time based on time to next energy.
    const timeToNextEnergy =
      (now - inventory.energyRechargeStartTime) %
      Inventory.getters(state).getEnergyRegenerationConfig().time;
    inventory.energyRechargeStartTime = now - timeToNextEnergy;
  },
});
